import React from "react";

// Normalise a user-pasted URL into a safe iframe `src` per embed type.
// Returns null when the URL doesn't look right — the component then shows
// a graceful fallback (link) instead of a broken iframe.
const buildEmbedSrc = (type, url) => {
  if (!url) return null;
  const u = url.trim();
  try {
    switch (type) {
      case "figma": {
        // Any Figma file / prototype URL → wrap with embed.figma.com
        if (u.startsWith("https://embed.figma.com/")) return u;
        if (u.includes("figma.com/")) {
          return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(u)}`;
        }
        return null;
      }
      case "loom": {
        // Accept both share and embed URLs; normalise to /embed/<id>
        const m = u.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
        if (m) return `https://www.loom.com/embed/${m[1]}`;
        return null;
      }
      case "youtube": {
        // Accept youtu.be/<id>, youtube.com/watch?v=<id>, /embed/<id>
        let id = null;
        const short = u.match(/youtu\.be\/([\w-]{6,})/);
        const watch = u.match(/[?&]v=([\w-]{6,})/);
        const embed = u.match(/youtube\.com\/embed\/([\w-]{6,})/);
        if (short) id = short[1];
        else if (watch) id = watch[1];
        else if (embed) id = embed[1];
        if (id) return `https://www.youtube.com/embed/${id}`;
        return null;
      }
      case "spotify": {
        // Accept https://open.spotify.com/<type>/<id> → embed variant
        const m = u.match(/open\.spotify\.com\/(episode|track|playlist|show|album)\/([\w-]+)/);
        if (m) return `https://open.spotify.com/embed/${m[1]}/${m[2]}`;
        return null;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
};

// Twitter / X posts are best rendered via twitter's official widget which
// requires an <a> anchor + async widgets.js. We keep this simple: render
// the tweet URL as a blockquote that the widget converts on mount.
const TwitterEmbed = ({ url }) => {
  React.useEffect(() => {
    if (window.twttr?.widgets) {
      window.twttr.widgets.load();
      return;
    }
    if (document.getElementById("twitter-widgets-js")) return;
    const s = document.createElement("script");
    s.id = "twitter-widgets-js";
    s.src = "https://platform.twitter.com/widgets.js";
    s.async = true;
    document.body.appendChild(s);
  }, [url]);
  return (
    <blockquote className="twitter-tweet" data-dnt="true">
      <a href={url}>{url}</a>
    </blockquote>
  );
};

export const EmbedBlock = ({ embed }) => {
  const { type, url, caption } = embed || {};
  if (!url) return null;

  const src = buildEmbedSrc(type, url);
  const aspect = type === "spotify" ? "aspect-[3/1]" : "aspect-video";

  return (
    <figure
      data-testid={`embed-${type}`}
      className="my-8 border border-black/10 bg-white overflow-hidden"
    >
      {type === "twitter" ? (
        <div className="p-4 flex justify-center">
          <TwitterEmbed url={url} />
        </div>
      ) : src ? (
        <div className={`w-full ${aspect}`}>
          <iframe
            src={src}
            title={caption || `${type} embed`}
            className="w-full h-full block"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="p-4">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] underline break-all"
          >
            Open {type} →
          </a>
        </div>
      )}
      {caption && (
        <figcaption className="px-4 py-2 border-t border-black/10 text-xs italic text-zinc-500 leading-relaxed">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

export default EmbedBlock;
